'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  PlusCircleIcon, BriefcaseIcon, MapPinIcon, BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { TailwindSpinner, bjToast } from '@components/common';
import { SidebarAccountRow } from '@components/layout/SidebarAccountRow';
import { SidebarContextSwitch } from '@components/layout/SidebarContextSwitch';
import {
  useSwrJobInterests,
  useSwrLocationInterests,
  useSwrCompanyInterests,
  type InterestData,
} from '@hooks/useSwrApi/endpoints/interests';
import { useSidebarNavigation, CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { fetcher, isApiV1Errors } from '@utils/data-fetching';
import { slugify } from '@utils/strings/slugify';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { JobSearchQueryParams } from '@type/filters';
import { MemoizedCompactJobAlertCard } from './CompactJobAlertCard';
import { POPULAR_SEARCH_KEYS } from './popularSearchKeys';
import { GuestJobsSidebar } from './GuestJobsSidebar';

const ITEMS_MAX = 3; // Show up to 3 items per interest section (job / location / company)

interface CandidateJobsSidebarProps {
  locale: Locale;
  className?: string;
  isAuthenticated?: boolean;
  showAccountSettings?: boolean;
  onSwitchContext?: () => void;
  /** Opens the Jobs on Map modal. Provided by MissionControl to avoid import cycles. */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

/**
 * @description Inner component that displays job alerts for authenticated candidates.
 */
const CandidateJobsSidebarContent = (props: Omit<CandidateJobsSidebarProps, 'isAuthenticated'>) => {
  const {
    locale,
    className,
    showAccountSettings = true,
    onSwitchContext,
    onOpenMap,
  } = props;
  const { t } = useTranslation('common');

  // Get navigation context
  const {
    currentView, navigateToNewJobSearchForm, navigateToCandidateSearch, navigateToKeywordSearch, navigateToEditSearch,
  } = useSidebarNavigation();

  const dispatchMobileClose = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      window.dispatchEvent(new Event('close-sidebar'));
      window.dispatchEvent(new Event('close-detail-modal'));
    }
  };

  const handleNavigateToNewJobSearchForm = () => { dispatchMobileClose(); navigateToNewJobSearchForm(); };
  const handleNavigateToAlert = (filters: CandidateSearchFilters) => { dispatchMobileClose(); navigateToCandidateSearch(filters); };
  const handleNavigateToKeywordSearch = (keyword: string) => { dispatchMobileClose(); navigateToKeywordSearch(keyword); };

  // Fetch unified next jobs data (combines job alerts and applications)
  const { data: jobInterestsData, isLoading, mutate: mutateJobInterests } = useSwrJobInterests({ locale });
  const { data: locationInterestsData, mutate: mutateLocationInterests } = useSwrLocationInterests({ locale });
  const { data: companyInterestsData, mutate: mutateCompanyInterests } = useSwrCompanyInterests({ locale });

  // Build combined list of all cards from NextJobData
  const allCards = useMemo(() => {
    if (!jobInterestsData || jobInterestsData.length === 0) return [];

    return jobInterestsData.slice(0, ITEMS_MAX).map((item) => ({
      id: `next-job-${item.id}`,
      nextJobId: typeof item.id === 'number' ? item.id : undefined,
      isAlert: item.sourceType === 'job_search_alert',
      title: item.jobTitle,
      keyword: item.jobTitle,
      location: item.locations?.[0],
      locations: item.locations,
      salary: item.salary ?? undefined,
      originalKeyword: item.originalKeyword ?? undefined,
      decomposedFilters: item.decomposedFilters,
    }));
  }, [jobInterestsData]);

  const locationCards = useMemo<InterestData[]>(
    () => [...(locationInterestsData ?? [])].sort((a, b) => b.weight - a.weight).slice(0, ITEMS_MAX),
    [locationInterestsData],
  );

  const companyCards = useMemo<InterestData[]>(
    () => [...(companyInterestsData ?? [])].sort((a, b) => b.weight - a.weight).slice(0, ITEMS_MAX),
    [companyInterestsData],
  );

  const handleDeleteJobInterest = (id: number) => {
    void fetcher<object>(`/v1/user/job-interests/${id}`, locale, { method: 'DELETE' }).then((response) => {
      if (isApiV1Errors(response)) {
        bjToast.error(response.errors[0].message);
        return;
      }
      void mutateJobInterests();
    });
  };

  const handleDeleteInterest = (basePath: string, id: number, revalidate: () => void) => {
    void fetcher<object>(`${basePath}/${id}`, locale, { method: 'DELETE' }).then((response) => {
      if (isApiV1Errors(response)) {
        bjToast.error(response.errors[0].message);
        return;
      }
      revalidate();
    });
  };

  // Build a /mc/job/list URL for a search — mirrors buildViewUrl in SidebarNavigationContext
  // so the card's anchor href matches the soft navigation. Locations go in the path segment
  // (location-<slug>), keyword is appended; `?edit=1` opens the edit form instead of results.
  const buildSearchHref = (filters: { keyword?: string; locations?: string[] }, isEdit = false) => {
    const { keyword, locations } = filters;
    const keywordSlug = keyword ? `/${slugify(keyword)}` : '';
    const base = locations?.length
      ? getI18nLinkHref(`/mc/job/list/location-${locations.map(slugify).join(',')}`, locale).replace(/%2C/g, ',')
      : getI18nLinkHref('/mc/job/list', locale);
    return `${base}${keywordSlug}${isEdit ? '?edit=1' : ''}`;
  };

  // Derive the active candidateSearch keyword/locations from context for selection highlighting
  const activeCandidateSearch = useMemo(() => (
    typeof currentView === 'object' && 'candidateSearch' in currentView ? currentView.candidateSearch : undefined
  ), [currentView]);

  // Determine selected keyword search (for keyword cards like last experience / next job)
  const selectedKeyword = useMemo(() => (typeof currentView === 'object' && 'keyword' in currentView ? currentView.keyword : undefined), [currentView]);

  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      {/* Top content - scrollable */}
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pb-4 pt-0">
        {/* Navigation Header - Links on top */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* Role-searched - Show new job search form in main content */}
          <button
            type="button"
            onClick={handleNavigateToNewJobSearchForm}
            className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-150"
            data-test-id="sidebar-role-searched"
          >
            <PlusCircleIcon className="size-5 text-ink" />
            <span>{t('mc.search.job') || 'Search job'}</span>
          </button>
        </div>

        {/* Job Search Alerts Section */}
        <div className="mb-3">
          {!isLoading && (
            <h3 className="mb-3 flex items-center gap-2 px-3 text-sm font-bold text-ink">
              <BriefcaseIcon className="size-4 shrink-0 text-ink" />
              {allCards.length === 0 ? t('user.popular.searches') : t('mc.interest.job.label')}
            </h3>
          )}

          {/* Combined list: Saved Alerts + Applications from NextJobData */}
          <div className="flex flex-col gap-1">
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <TailwindSpinner className="size-6 text-primary" />
              </div>
            )}

            {/* Empty state — show popular searches when nothing is available */}
            {allCards.length === 0 && !isLoading && (
              <>
                {POPULAR_SEARCH_KEYS.slice(0, 6).map((key) => {
                  const keyword = t(key);
                  const isSelected = keyword === selectedKeyword;
                  return (
                    <MemoizedCompactJobAlertCard
                      key={key}
                      title={keyword}
                      locale={locale}
                      isSelected={isSelected}
                      onSelect={() => handleNavigateToKeywordSearch(keyword)}
                      onOpenMap={onOpenMap}
                    />
                  );
                })}
              </>
            )}

            {/* All cards from NextJobData */}
            {allCards.map((card) => {
              const isSelected = card.isAlert
                ? activeCandidateSearch?.keyword === card.keyword
                  && JSON.stringify(activeCandidateSearch?.locations) === JSON.stringify(card.locations)
                : card.keyword === selectedKeyword;

              const handleSelect = () => {
                if (card.isAlert && card.keyword) {
                  const filters: CandidateSearchFilters = {
                    keyword: card.keyword,
                    ...(card.locations && { locations: card.locations }),
                  };
                  handleNavigateToAlert(filters);
                } else if (card.keyword) {
                  handleNavigateToKeywordSearch(card.keyword);
                }
              };

              const handleEdit = () => {
                if (card.keyword) {
                  navigateToEditSearch({
                    keyword: card.keyword,
                    ...(card.locations && { locations: card.locations }),
                    ...(card.salary && { salary: card.salary }),
                    ...(card.originalKeyword && { originalKeyword: card.originalKeyword }),
                    ...(card.decomposedFilters && { decomposedFilters: card.decomposedFilters }),
                  });
                }
              };

              const editHref = buildSearchHref({ keyword: card.keyword, locations: card.locations }, true);

              return (
                <MemoizedCompactJobAlertCard
                  key={card.id}
                  title={card.title}
                  locale={locale}
                  isSelected={isSelected}
                  location={card.location}
                  onSelect={handleSelect}
                  onEdit={card.keyword ? handleEdit : undefined}
                  editHref={editHref}
                  onOpenMap={onOpenMap}
                  onDelete={card.nextJobId !== undefined ? () => handleDeleteJobInterest(card.nextJobId!) : undefined}
                />
              );
            })}
          </div>
        </div>

        {/* Location interests */}
        {locationCards.length > 0 && (
          <div className="mb-3">
            <h3 className="mb-3 flex items-center gap-2 px-3 text-sm font-bold text-ink">
              <MapPinIcon className="size-4 shrink-0 text-ink" />
              {t('mc.interest.location.label')}
            </h3>
            <div className="flex flex-col gap-1">
              {locationCards.map((item) => {
                // A location interest is a location-only search: it goes in the path segment
                // (location-<slug>), never the keyword slot.
                const isSelected = !activeCandidateSearch?.keyword
                  && activeCandidateSearch?.locations?.length === 1
                  && activeCandidateSearch.locations[0] === item.value;
                return (
                  <MemoizedCompactJobAlertCard
                    key={`location-interest-${item.id}`}
                    title={item.value}
                    locale={locale}
                    isSelected={isSelected}
                    href={buildSearchHref({ locations: [item.value] })}
                    onSelect={() => handleNavigateToAlert({ locations: [item.value] })}
                    onEdit={() => navigateToEditSearch({ keyword: item.value })}
                    editHref={buildSearchHref({ keyword: item.value }, true)}
                    onOpenMap={onOpenMap}
                    mapQueryParams={{ location: [item.value] }}
                    onDelete={() => handleDeleteInterest('/v1/user/location-interests', item.id, () => { void mutateLocationInterests(); })}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Company interests */}
        {companyCards.length > 0 && (
          <div className="mb-3">
            <h3 className="mb-3 flex items-center gap-2 px-3 text-sm font-bold text-ink">
              <BuildingOffice2Icon className="size-4 shrink-0 text-ink" />
              {t('mc.interest.company.label')}
            </h3>
            <div className="flex flex-col gap-1">
              {companyCards.map((item) => (
                <MemoizedCompactJobAlertCard
                  key={`company-interest-${item.id}`}
                  title={item.value}
                  locale={locale}
                  isSelected={item.value === selectedKeyword}
                  href={buildSearchHref({ keyword: item.value })}
                  onSelect={() => handleNavigateToKeywordSearch(item.value)}
                  onEdit={() => navigateToEditSearch({ keyword: item.value })}
                  editHref={buildSearchHref({ keyword: item.value }, true)}
                  onOpenMap={onOpenMap}
                  onDelete={() => handleDeleteInterest('/v1/user/company-interests', item.id, () => { void mutateCompanyInterests(); })}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Always-visible context toggle (Caut job / Angajez); also available in the account popup */}
      {onSwitchContext && (
        <div className="px-2 pb-2">
          <SidebarContextSwitch activeContext="candidate" onSwitch={onSwitchContext} />
        </div>
      )}

      {showAccountSettings && <SidebarAccountRow />}
    </div>
  );
};

/**
 * @description Modular job alerts list sidebar component for candidates.
 * Displays saved job search alerts with "New Job Search" card at top.
 * Shows a sign-in prompt when user is not authenticated.
 */
export const CandidateJobsSidebar = (props: CandidateJobsSidebarProps) => {
  const { isAuthenticated = false, ...restProps } = props;

  // Show authenticated content if user is logged in
  if (isAuthenticated) {
    return <CandidateJobsSidebarContent {...restProps} />;
  }

  return (
    <GuestJobsSidebar
      locale={restProps.locale}
      className={restProps.className}
      onOpenMap={restProps.onOpenMap}
    />
  );
};
